import { Box, Button, Flex, Text } from '@weareberyl/design-system'
import { useState } from 'react'
import { message } from 'antd'

import { SuspendModal } from '~/components/Zone/SuspendModal'
import { useBulkZonePromotionalMutation } from './useBulkZonePromotionalMutation'
import { useBulkZoneStatusMutation } from './useBulkZoneStatusMutation'
import {
  useZoneScheduledSuspendMutation,
  ZoneSuspensionInfoInput,
  ZoneSuspensionReasonInput,
} from '~/gql/generated/graphql'
import { useCurrentScheme } from '~/hooks'
import type { TabOptions } from '.'
import { ScheduledSuspendModal } from '../ScheduledSuspendModal'
import { type BulkAction, type BulkActionCtx } from './bulkAction'
import { useSelectedZones } from './useSelectedZones'
import { useBulkZonePriorityMutation } from './useBulkZonePriorityMutation'

type Props = {
  selectedRowKeys: string[]
  resetProperties: (currentTab: TabOptions, ids: string[]) => void
  currentTab: TabOptions
}

const toastBulkResult = (successCopy: string) => (failed: string[]) => {
  if (failed.length === 1) {
    message.error(`Failed to update for bay with ID: '${failed[0]}'`)
  } else if (failed.length > 1) {
    message.error('Failed to update multiple bays')
  } else {
    message.success(successCopy)
  }
}

const EditSelectedZones = ({
  selectedRowKeys,
  resetProperties,
  currentTab,
}: Props) => {
  const isSuspended = currentTab === 'suspended'
  const { currentSchemeId } = useCurrentScheme()

  const [modalState, setModalState] = useState<{
    action: BulkAction<unknown> | null
  }>({ action: null })
  const closeModal = () => setModalState({ action: null })

  // Various mutation hooks

  const [updateSuspended, suspendedLoading] = useBulkZoneStatusMutation({
    isSuspended,
    zoneIds: selectedRowKeys,
    onCompleted: toastBulkResult('Bay statuses successfully updated'),
    onError: (err: unknown) => message.error(String(err)),
  })

  const [scheduleSuspend, { loading: scheduleSuspendLoading }] =
    useZoneScheduledSuspendMutation({
      refetchQueries: ['ZonesList'],
      onCompleted: _ =>
        toastBulkResult('Schedule has been set successfully')([]),
      onError: (err: unknown) => message.error(String(err)),
    })

  const [updatePromotional, promotionalLoading] =
    useBulkZonePromotionalMutation({
      onCompleted: toastBulkResult('Promotional flags successfully updated'),
      onError: (err: unknown) => message.error(String(err)),
    })

  const [updatePriority, priorityLoading] = useBulkZonePriorityMutation({
    onCompleted: toastBulkResult('Priority flags successfully updated'),
    onError: (err: unknown) => message.error(String(err)),
  })

  // Grab info about selected ids from apollo cache, and calculate some stats
  const { zones, statusStats } = useSelectedZones(selectedRowKeys)

  // Shared context for each action
  const actionCtx: BulkActionCtx = {
    selectedIds: selectedRowKeys,
    selectedZones: zones,
    viewingSuspended: isSuspended,
    statusStats,
    schemeId: currentSchemeId,
  }

  // Any mutation currently in flight
  const anyLoading =
    suspendedLoading || promotionalLoading || scheduleSuspendLoading

  // Attach loading status to the correct action label
  const actionLoadingMap: Record<BulkAction['key'], boolean> = {
    suspend: suspendedLoading,
    enable: suspendedLoading,
    'schedule-suspend': scheduleSuspendLoading,
    promotional: promotionalLoading,
    'non-promotional': promotionalLoading,
    priority: priorityLoading,
    non_priority: priorityLoading,
  }

  // Config for each action (button)

  // suspend and scheduled suspend

  const actionSuspend: BulkAction<ZoneSuspensionInfoInput> = {
    key: 'suspend',
    buttonLabel: 'Suspend',
    buttonVariant: 'danger',
    isVisible: ctx => !ctx.viewingSuspended,
    requiresModal: true,
    Modal: SuspendModal,
    run: async (ctx, payload) => {
      await updateSuspended({
        isSuspended: false,
        zoneIds: ctx.selectedIds,
        suspensionReason: payload,
      })
    },
  }

  const actionScheduledSuspend: BulkAction<{
    start_time: string
    end_time: string
    notes?: string
    reason: ZoneSuspensionReasonInput
  }> = {
    key: 'schedule-suspend',
    buttonLabel: 'Schedule Suspension',
    buttonVariant: 'danger',
    isVisible: ctx => !ctx.viewingSuspended,
    requiresModal: true,
    Modal: ScheduledSuspendModal,
    run: async (ctx, payload) => {
      if (payload && ctx.schemeId) {
        await scheduleSuspend({
          variables: {
            zone_ids: ctx.selectedIds,
            scheme_id: ctx.schemeId,
            ...payload,
          },
        })
      }
    },
  }

  // unsuspend / enable

  const actionEnable: BulkAction = {
    key: 'enable',
    buttonLabel: 'Enable',
    buttonVariant: 'primary',
    isVisible: ctx => ctx.viewingSuspended,
    run: async ctx => {
      await updateSuspended({ zoneIds: ctx.selectedIds, isSuspended: false })
      resetProperties(currentTab, [])
    },
  }

  // promo / non-promo

  const actionPromotional: BulkAction = {
    key: 'promotional',
    buttonLabel: 'Set Promotional',
    buttonVariant: 'primary',
    isVisible: ctx => ctx.statusStats.notPromotional > 0,
    run: async ctx => {
      await updatePromotional({ zoneIds: ctx.selectedIds, isPromotional: true })
    },
  }

  const actionNonPromotional: BulkAction = {
    key: 'non-promotional',
    buttonLabel: 'Set not promotional',
    buttonVariant: 'outline',
    isVisible: ctx => ctx.statusStats.promotional > 0,
    run: async ctx => {
      await updatePromotional({
        zoneIds: ctx.selectedIds,
        isPromotional: false,
      })
    },
  }

  const actionPriority: BulkAction = {
    key: 'priority',
    buttonLabel: 'Set priority',
    buttonVariant: 'primary',
    isVisible: ctx => ctx.statusStats.notPriority > 0,
    run: async ctx => {
      await updatePriority({
        zoneIds: ctx.selectedIds,
        isPriority: true,
      })
    },
  }

  const actionNonPriority: BulkAction = {
    key: 'non_priority',
    buttonLabel: 'Set not priority',
    buttonVariant: 'outline',
    isVisible: ctx => ctx.statusStats.priority > 0,
    run: async ctx => {
      await updatePriority({
        zoneIds: ctx.selectedIds,
        isPriority: false,
      })
    },
  }
  const bulkActions: BulkAction<unknown>[] = [
    actionSuspend,
    actionScheduledSuspend,
    actionEnable,
    actionPromotional,
    actionNonPromotional,
    actionPriority,
    actionNonPriority,
  ]

  return (
    <>
      {/** Render the modal that belongs to the selected action (if any) */}
      {modalState.action?.Modal && modalState.action.requiresModal && (
        <modalState.action.Modal
          open
          count={selectedRowKeys.length}
          onCancel={closeModal}
          onConfirm={async (payload: unknown) => {
            const { action } = modalState
            if (!action) return

            await action.run(actionCtx, payload)
            closeModal()
          }}
        />
      )}

      <Flex
        mt={2}
        gridGap="10"
        alignItems="baseline"
        justifyContent="flex-start"
      >
        {/** Render any available actions */}
        <Text flex={1}>
          {selectedRowKeys.length > 0
            ? `Update ${selectedRowKeys.length} selected bays:`
            : ''}
        </Text>
        {selectedRowKeys.length > 0 && (
          <Box flex={3} flexDirection="row" flexWrap="wrap" style={{ gap: 5 }}>
            {bulkActions.map(
              action =>
                action.isVisible(actionCtx) && (
                  <Button
                    key={action.key}
                    title={action.buttonLabel}
                    onPress={async () => {
                      if (action.requiresModal) {
                        setModalState({ action })
                      } else {
                        await action.run(actionCtx)
                      }
                    }}
                    loading={actionLoadingMap[action.buttonLabel]}
                    disabled={anyLoading}
                    variant={action.buttonVariant}
                    size="mini"
                    px={3}
                    py={2}
                  />
                ),
            )}
          </Box>
        )}
        <Flex flex={1} justifyContent="flex-end">
          <Button
            title="Clear selection"
            onPress={() => resetProperties(currentTab, [])}
            variant="outline"
            // @ts-expect-error
            size="small"
            px={3}
            py={2}
            disabled={selectedRowKeys.length === 0 || anyLoading}
          />
        </Flex>
      </Flex>
    </>
  )
}

export default EditSelectedZones
